import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { Helmet } from 'react-helmet';
import {
  MapPin,
  Clock,
  Umbrella,
  Beer,
  Info,
  Calendar,
  Camera,
  Accessibility,
  Dog,
  Car,
  Utensils,
  Home,
  ArrowRight,
  Share2,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
  EmailIcon,
} from 'react-share';
import { ThingsToDoData } from '../../types/thingsToDo';
import { Loader2 } from 'lucide-react';

const ThingsToDoPage: React.FC = () => {
  const { county } = useParams();
  const [data, setData] = useState<ThingsToDoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!county) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const countyId = `co-${county.toLowerCase()}`;
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getThingsToDo?countyId=${countyId}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          setData(result.data);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [county]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Guide Not Found</h1>
        <p className="text-slate-600 mb-6">We couldn't find a guide for {county}.</p>
        <Link to="/" className="text-blue-600 hover:underline">
          Return Home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{data.meta_tags.title}</title>
        <meta name="description" content={data.meta_tags.description} />
        <meta name="keywords" content={data.meta_tags.keywords} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'TouristDestination',
            name: data.county_name,
            description: data.hero.summary,
            touristType: ['AdventureTourism', 'CulturalTourism'],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
        {/* HERO SECTION */}
        <div className="relative bg-[#1B365D] text-white py-16 sm:py-24 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/10 rounded-full text-xs font-semibold tracking-wider uppercase mb-4">
              Local Guide 2026
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold mb-6 leading-tight">
              {data.hero.headline}
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto mb-8 font-light leading-relaxed">
              {data.hero.summary}
            </p>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12 bg-white/5 rounded-xl p-4 sm:p-6 backdrop-blur-sm border border-white/10">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-yellow-300 text-sm font-semibold uppercase tracking-wider mb-1">
                  <MapPin className="h-4 w-4" /> Top Spot
                </div>
                <div className="font-bold text-white text-sm sm:text-base">
                  {data.quick_stats.top_attraction}
                </div>
              </div>
              <div className="text-center border-l border-white/10">
                <div className="flex items-center justify-center gap-1 text-blue-300 text-sm font-semibold uppercase tracking-wider mb-1">
                  <Clock className="h-4 w-4" /> Duration
                </div>
                <div className="font-bold text-white text-sm sm:text-base">
                  {data.quick_stats.avg_visit_duration}
                </div>
              </div>
              {data.quick_stats.local_pint_price && (
                <div className="text-center border-l border-white/10 hidden sm:block">
                  <div className="flex items-center justify-center gap-1 text-green-300 text-sm font-semibold uppercase tracking-wider mb-1">
                    <Beer className="h-4 w-4" /> Pint Index
                  </div>
                  <div className="font-bold text-white text-sm sm:text-base">
                    {data.quick_stats.local_pint_price}
                  </div>
                </div>
              )}
              {data.quick_stats.rainy_days_avg && (
                <div className="text-center border-l border-white/10 hidden sm:block">
                  <div className="flex items-center justify-center gap-1 text-slate-300 text-sm font-semibold uppercase tracking-wider mb-1">
                    <Umbrella className="h-4 w-4" /> Rainy Days
                  </div>
                  <div className="font-bold text-white text-sm sm:text-base">
                    {data.quick_stats.rainy_days_avg}
                  </div>
                </div>
              )}
            </div>

            {/* Social Share */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <span className="flex items-center gap-2 text-blue-200 text-sm font-medium">
                <Share2 className="h-4 w-4" /> Share Guide:
              </span>
              <div className="flex gap-3">
                <FacebookShareButton url={`https://rentsmart.ie/things-to-do/${data.slug}`}>
                  <FacebookIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </FacebookShareButton>
                <TwitterShareButton
                  url={`https://rentsmart.ie/things-to-do/${data.slug}`}
                  title={`Discover the best things to do in ${data.county_name}!`}
                >
                  <TwitterIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </TwitterShareButton>
                <WhatsappShareButton
                  url={`https://rentsmart.ie/things-to-do/${data.slug}`}
                  title={`Check out this guide for things to do in ${data.county_name}!`}
                >
                  <WhatsappIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </WhatsappShareButton>
                <LinkedinShareButton
                  url={`https://rentsmart.ie/things-to-do/${data.slug}`}
                  title={`Things to Do in ${data.county_name} - Ultimate Travel Guide`}
                >
                  <LinkedinIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </LinkedinShareButton>
                <EmailShareButton
                  url={`https://rentsmart.ie/things-to-do/${data.slug}`}
                  subject={`Things to Do in ${data.county_name} Guide`}
                  body={`Check out this ultimate guide to things to do in ${data.county_name}: `}
                >
                  <EmailIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </EmailShareButton>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          {/* ITINERARIES */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Calendar className="h-6 w-6 text-blue-600" /> Curated Itineraries
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* One Day */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-lg mb-3 text-slate-800">One Day Express</h3>
                <ul className="space-y-2">
                  {data.itineraries.one_day_express.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <div className="min-w-[20px] h-[20px] rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mt-0.5">
                        {i + 1}
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weekend */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-lg mb-3 text-slate-800">Family Weekend</h3>
                <ul className="space-y-2">
                  {data.itineraries.family_weekend.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <div className="min-w-[20px] h-[20px] rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold mt-0.5">
                        {i + 1}
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hidden Gems */}
              {data.itineraries.hidden_gems_trail && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                  <h3 className="font-bold text-lg mb-3 text-slate-800">Hidden Gems Trail</h3>
                  <ul className="space-y-2">
                    {data.itineraries.hidden_gems_trail.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <div className="min-w-[20px] h-[20px] rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold mt-0.5">
                          {i + 1}
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* CATEGORIES */}
          {data.categories.map((cat, idx) => (
            <div key={idx} className="mb-16">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                {cat.category_name}
              </h2>
              <div className="grid grid-cols-1 gap-6">
                {cat.attractions.map((attraction, aIdx) => (
                  <div
                    key={aIdx}
                    className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden flex flex-col md:flex-row hover:shadow-md transition-shadow"
                  >
                    <div className="p-6 md:p-8 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {attraction.is_free && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase rounded">
                            Free Entry
                          </span>
                        )}
                        {attraction.instagram_spot && (
                          <span className="px-2 py-1 bg-pink-100 text-pink-700 text-xs font-bold uppercase rounded flex items-center gap-1">
                            <Camera className="h-3 w-3" /> Insta-Ready
                          </span>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-slate-900 mb-2">{attraction.name}</h3>
                      <p className="text-slate-600 mb-4 leading-relaxed">
                        {attraction.description}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-6">
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-4 w-4" /> {attraction.typical_time_spent}
                        </span>
                        {attraction.dog_friendly && (
                          <span className="flex items-center gap-1.5 text-slate-700">
                            <Dog className="h-4 w-4" /> Dog Friendly
                          </span>
                        )}
                        {attraction.wheelchair_accessible && (
                          <span className="flex items-center gap-1.5 text-slate-700">
                            <Accessibility className="h-4 w-4" /> Accessible
                          </span>
                        )}
                      </div>

                      <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-sm text-blue-900 border border-blue-100">
                        <span className="font-bold flex items-center gap-1.5 mb-1 text-blue-700">
                          <Info className="h-4 w-4" /> Local Pro Tip:
                        </span>
                        {attraction.pro_tip}
                      </div>
                    </div>

                    {/* Sidebar / Map Placeholder */}
                    {attraction.eircode_routing && (
                      <div className="bg-slate-50 p-6 md:w-64 border-l border-slate-100 flex flex-col justify-center">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                          Navigator
                        </div>
                        <div className="text-2xl font-mono font-bold text-slate-700 mb-1">
                          {attraction.eircode_routing}
                        </div>
                        <div className="text-sm text-slate-500 mb-4">Routing Key Area</div>
                        <button
                          onClick={() => {
                            const query = attraction.geo
                              ? `${attraction.geo.lat},${attraction.geo.lng}`
                              : encodeURIComponent(`${attraction.name} ${data.county_name}`);
                            window.open(
                              `https://www.google.com/maps/search/?api=1&query=${query}`,
                              '_blank'
                            );
                          }}
                          className="w-full text-center py-2 bg-white border border-slate-200 rounded text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                        >
                          View on Map
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* EVENTS & FOOD */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {data.seasonal_events && data.seasonal_events.length > 0 && (
              <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-xl border border-indigo-100">
                <h3 className="font-bold text-lg mb-4 text-indigo-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5" /> Seasonal Highlights
                </h3>
                <div className="space-y-4">
                  {data.seasonal_events.map((evt, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="px-2 py-1 bg-white rounded border border-indigo-100 text-center min-w-[50px] h-fit">
                        <span className="block text-xs font-bold text-slate-400 uppercase">
                          {evt.month.substring(0, 3)}
                        </span>
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{evt.event_name}</div>
                        <div className="text-sm text-slate-600 leading-snug">{evt.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-6">
              {data.local_food_to_try && (
                <div className="bg-orange-50 p-6 rounded-xl border border-orange-100">
                  <h3 className="font-bold text-lg mb-2 text-orange-900 flex items-center gap-2">
                    <Utensils className="h-5 w-5" /> Must Try Food
                  </h3>
                  <p className="text-orange-900/80 leading-relaxed">{data.local_food_to_try}</p>
                </div>
              )}
              {data.local_transport_tip && (
                <div className="bg-slate-100 p-6 rounded-xl border border-slate-200">
                  <h3 className="font-bold text-lg mb-2 text-slate-900 flex items-center gap-2">
                    <Car className="h-5 w-5" /> Getting Around
                  </h3>
                  <p className="text-slate-700 leading-relaxed">{data.local_transport_tip}</p>
                </div>
              )}
            </div>
          </div>

          {/* FAQs */}
          <div className="border-t border-slate-200 pt-16">
            <h2 className="text-2xl font-bold mb-8 text-center text-slate-900">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {data.faqs.map((faq, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-20 relative overflow-hidden rounded-3xl bg-[#1B365D] text-white shadow-2xl">
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-indigo-500/20 blur-3xl"></div>

            <div className="relative z-10 px-8 py-16 sm:px-16 text-center">
              <div className="inline-flex items-center justify-center p-4 bg-white/10 rounded-2xl mb-8 backdrop-blur-sm border border-white/10 shadow-inner">
                <Home className="h-10 w-10 text-blue-200" />
              </div>

              <h2 className="text-3xl sm:text-5xl font-bold mb-6 tracking-tight">
                Make {data.county_name} Your Home
              </h2>
              <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
                Fell in love with the local vibe? Explore curated rental properties in{' '}
                {data.county_name}'s best neighborhoods.
              </p>

              <a
                href={`/browse?location=${encodeURIComponent(data.county_name || county || 'dublin')}`}
                className="no-underline inline-flex items-center gap-3 px-10 py-5 bg-white text-[#1B365D] font-bold text-lg rounded-xl hover:bg-blue-50 transition-all shadow-[0_20px_40px_-15px_rgba(0,0,0,0.3)] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)] hover:-translate-y-1 group"
              >
                Browse Properties in {data.county_name}
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThingsToDoPage;
